// react-native-svg on the web, with two fixes so SVGs look as they do on iOS
// and Android:
//
// 1. Size: on mobile an <Svg> without width/height fills the box its style
//    gives it (e.g. StyleSheet.absoluteFill), but a browser <svg> falls back
//    to 300x150. Default both to 100%. A width/height in `style` still wins.
//
// 2. IDs: on mobile each SVG is isolated, but in a browser every SVG shares
//    the page's ID namespace. SVGR shortens ids to `a`, `b`, ..., and screens
//    further down the navigation stack stay mounted (hidden), so `url(#a)`
//    can resolve to a gradient or image inside another, hidden SVG and draw
//    nothing. Each <Svg> instance gets its own id suffix after rendering.
import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import BaseSvg from 'react-native-svg/lib/module/index';

export * from 'react-native-svg/lib/module/index';

const XLINK = 'http://www.w3.org/1999/xlink';
const REF_ATTRS = [
  'fill',
  'stroke',
  'clip-path',
  'mask',
  'filter',
  'marker-start',
  'marker-mid',
  'marker-end',
  'href',
  'xlink:href',
];

let instances = 0;

function scopeIds(svg, suffix) {
  const withId = svg.querySelectorAll('[id]');
  if (!withId.length) {
    return;
  }
  // Original id -> scoped id. Already-scoped ids are mapped back to their
  // original, so references React re-renders unscoped get fixed again.
  const ids = new Map();
  withId.forEach(el => {
    const id = el.getAttribute('id');
    const original = id.endsWith(suffix) ? id.slice(0, -suffix.length) : id;
    ids.set(original, original + suffix);
    if (id !== original + suffix) {
      el.setAttribute('id', original + suffix);
    }
  });
  const scope = (match, id, before, after) =>
    ids.has(id) ? `${before}${ids.get(id)}${after}` : match;
  svg.querySelectorAll('*').forEach(el => {
    for (const attr of REF_ATTRS) {
      const value = el.getAttribute(attr);
      if (!value || !value.includes('#')) {
        continue;
      }
      const next = value
        .replace(/(url\(#)([^)]+)(\))/g, (m, before, id, after) =>
          scope(m, id, before, after),
        )
        .replace(/^(#)(.+)()$/, (m, before, id, after) =>
          scope(m, id, before, after),
        );
      if (next !== value) {
        if (attr === 'xlink:href') {
          el.setAttributeNS(XLINK, attr, next);
        } else {
          el.setAttribute(attr, next);
        }
      }
    }
  });
}

const Svg = forwardRef(function Svg(
  {width = '100%', height = '100%', ...props},
  ref,
) {
  const svgRef = useRef(null);
  const [suffix] = useState(() => `-s${++instances}`);
  useImperativeHandle(ref, () => svgRef.current);

  // Runs after every render: React only rewrites attributes whose props
  // changed, and those come back unscoped.
  useLayoutEffect(() => {
    const element = svgRef.current?.elementRef?.current;
    if (element) {
      scopeIds(element, suffix);
    }
  });

  return <BaseSvg ref={svgRef} width={width} height={height} {...props} />;
});

export {Svg};
export default Svg;
